/**
 * Cancellation & Refund Policy Page
 *
 * Task: T4.3.4 - Policy pages
 */

import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  Box,
  Banner,
  DataTable,
} from '@shopify/polaris';

export function RefundPage() {
  const refundTimelines = [
    ['Before production starts', '100% (minus payment gateway fees)'],
    ['Production in progress', '50% (non-customized portions only)'],
    ['After production', 'No refund for customized items'],
    ['After dispatch', 'Subject to return acceptance'],
  ];

  return (
    <Page title="Cancellation & Refund Policy" narrowWidth>
      <Layout>
        <Layout.Section>
          <Card>
            <Box padding="600">
              <BlockStack gap="600">
                <Banner tone="info">
                  <Text as="p" variant="bodyMd">
                    Please read this policy carefully before placing an order. Customized products
                    have limited cancellation and refund options.
                  </Text>
                </Banner>

                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Order Cancellation
                  </Text>
                  <Text as="p" variant="bodyMd">
                    You may request to cancel your order by contacting our customer service team.
                    Cancellation acceptance depends on the order status:
                  </Text>
                  <Text as="p" variant="bodyMd">
                    • <strong>Before production:</strong> Full cancellation possible
                    <br />
                    • <strong>During production:</strong> Partial cancellation may be possible
                    <br />
                    • <strong>After dispatch:</strong> Cancellation not possible; return policy applies
                  </Text>
                </BlockStack>

                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Refund Policy
                  </Text>
                  <DataTable
                    columnContentTypes={['text', 'text']}
                    headings={['Order Status', 'Refund Amount']}
                    rows={refundTimelines}
                  />
                </BlockStack>

                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Non-Refundable Items
                  </Text>
                  <Text as="p" variant="bodyMd">
                    The following are generally non-refundable:
                  </Text>
                  <Text as="p" variant="bodyMd">
                    • Customized/personalized products with your logo or branding
                    <br />
                    • Products manufactured to custom specifications
                    <br />
                    • Sample products (unless defective)
                    <br />
                    • Products on special clearance sale
                  </Text>
                </BlockStack>

                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Returns and Exchanges
                  </Text>
                  <Text as="p" variant="bodyMd">
                    We accept returns in the following cases:
                  </Text>
                  <Text as="p" variant="bodyMd">
                    • <strong>Defective products:</strong> Manufacturing defects or quality issues
                    <br />
                    • <strong>Wrong items:</strong> Items different from what was ordered
                    <br />
                    • <strong>Damaged in transit:</strong> Products damaged during shipping
                  </Text>
                  <Text as="p" variant="bodyMd">
                    To initiate a return:
                  </Text>
                  <Text as="p" variant="bodyMd">
                    1. Contact us within 48 hours of delivery
                    <br />
                    2. Provide photos/videos of the issue
                    <br />
                    3. Keep original packaging intact
                    <br />
                    4. Wait for return authorization
                  </Text>
                </BlockStack>

                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Refund Process
                  </Text>
                  <Text as="p" variant="bodyMd">
                    Once a refund is approved:
                  </Text>
                  <Text as="p" variant="bodyMd">
                    • Refunds are processed within 7-10 business days
                    <br />
                    • Amount is credited to the original payment method
                    <br />
                    • Bank transfers may take additional 3-5 business days to reflect
                    <br />
                    • You will receive email confirmation once refund is processed
                  </Text>
                </BlockStack>

                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    Quality Guarantee
                  </Text>
                  <Text as="p" variant="bodyMd">
                    We stand behind the quality of our products. If you receive a defective item,
                    we will:
                  </Text>
                  <Text as="p" variant="bodyMd">
                    • Replace the item at no additional cost, OR
                    <br />
                    • Provide a full refund including shipping charges
                  </Text>
                </BlockStack>

                <BlockStack gap="400">
                  <Text as="h2" variant="headingMd">
                    How to Request Cancellation or Refund
                  </Text>
                  <Text as="p" variant="bodyMd">
                    Contact our customer service team:
                  </Text>
                  <Text as="p" variant="bodyMd">
                    Email: support@corporategifts.com
                    <br />
                    Phone: +91 98765 43210
                    <br />
                    Include your order number in all communications.
                  </Text>
                </BlockStack>
              </BlockStack>
            </Box>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
